import { z } from 'zod';

const ErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z
      .array(
        z.object({ field: z.string(), code: z.string(), message: z.string() }),
      )
      .optional(),
  }),
});

export class V1ApiError extends Error {
  readonly retryAt: number;

  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly details: { field: string; code: string; message: string }[] = [],
    retryAfterMs = 0,
  ) {
    super(message);
    this.name = 'V1ApiError';
    this.retryAt = retryAfterMs > 0 ? Date.now() + retryAfterMs : 0;
  }
}

export function isV1Request(url?: string) {
  return /(?:^|\/)v1\//.test(url ?? '');
}

export function v1ResponseError(
  status: number,
  body: unknown,
  retryAfter?: unknown,
) {
  const parsed = ErrorBodySchema.safeParse(body);
  const seconds = Number(retryAfter);
  // Cross-origin deployments must expose Retry-After; still back off if they do not.
  const retryAfterMs =
    Number.isFinite(seconds) && seconds > 0
      ? seconds * 1000
      : status === 429
        ? 10_000
        : 0;
  return new V1ApiError(
    parsed.success
      ? parsed.data.error.message
      : 'Unable to reach the payment service. Please try again.',
    status,
    parsed.success ? parsed.data.error.code : 'request_failed',
    parsed.success ? parsed.data.error.details : undefined,
    retryAfterMs,
  );
}

// V1 returns the resource itself, not the legacy { status, code, data } envelope.
export function parseV1<T>(schema: z.ZodType<T>, body: unknown): T {
  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new V1ApiError(
      'The payment service returned incomplete information. Please try again.',
      0,
      'invalid_response',
    );
  }
  return parsed.data;
}

export function canRetryV1(error: unknown) {
  return (
    !(error instanceof V1ApiError) ||
    (error.code !== 'invalid_response' &&
      (error.status === 0 || error.status === 429 || error.status >= 500))
  );
}
